'use client';

import React, { createContext, useContext } from 'react';

export type Nav = {
  push: (to: string) => void;
  replace: (to: string) => void;
  back: () => void;
};

export type QueryParamSetter = (
  value: string | null,
  opts?: { replace?: boolean },
) => void;

export type AppLinkProps = {
  href: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  target?: string;
  rel?: string;
  onClick?: React.MouseEventHandler;
  'aria-label'?: string;
  'data-testid'?: string;
};

export type AppImageProps = {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  priority?: boolean;
  className?: string;
  style?: React.CSSProperties;
  sizes?: string;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  'data-testid'?: string;
};

export type NavAdapter = {
  useNavImpl: () => Nav;
  useQueryParamImpl: (key: string) => [string | null, QueryParamSetter];
  Link: React.ComponentType<AppLinkProps>;
  Image: React.ComponentType<AppImageProps>;
};

const NavContext = createContext<NavAdapter | null>(null);

export function NavProvider({
  adapter,
  children,
}: {
  adapter: NavAdapter;
  children: React.ReactNode;
}) {
  return <NavContext.Provider value={adapter}>{children}</NavContext.Provider>;
}

function useAdapter(): NavAdapter {
  const adapter = useContext(NavContext);
  if (!adapter) {
    throw new Error(
      'NavProvider missing — wrap the app with NavProvider from @racefriends/shared/nav',
    );
  }
  return adapter;
}

export function useNav(): Nav {
  return useAdapter().useNavImpl();
}

export function useQueryParam(
  key: string,
): [string | null, QueryParamSetter] {
  return useAdapter().useQueryParamImpl(key);
}

export function AppLink(props: AppLinkProps) {
  const { Link } = useAdapter();
  return <Link {...props} />;
}

export function AppImage(props: AppImageProps) {
  const { Image } = useAdapter();
  return <Image {...props} />;
}
